import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/*
 * Keep the menu active while the pointer is inside the
 * upper 85% of the viewport.
 */
const MENU_DEACTIVATE_THRESHOLD = 0.85;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pointerIdleTimer;

  #state = {
    activeItem: null,
  };

  /**
   * Last known pointer position.
   */
  #lastPointer = {
    x: 0,
    y: 0,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener('resize', this.#resizeListener);

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    /*
     * IMPORTANT:
     * Global pointer tracking fixes the case where the mouse
     * enters a menu item from below and native pointerenter
     * is not fired correctly.
     */
    document.addEventListener(
      'pointermove',
      this.#globalPointerMove,
      { passive: true }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'resize',
      this.#resizeListener
    );

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    document.removeEventListener(
      'pointermove',
      this.#globalPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(
        this.#state.activeItem
      );
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Resize listener.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Overflow menu close.
   */
  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * =========================================================
   * GLOBAL POINTER FALLBACK
   * =========================================================
   *
   * This is the important fix.
   *
   * It detects the actual element underneath the pointer
   * instead of depending only on pointerenter.
   */
  #globalPointerMove = (event) => {
    if (!(event.target instanceof Element)) return;

    const x = event.clientX;
    const y = event.clientY;

    this.#lastPointer.x = x;
    this.#lastPointer.y = y;

    /*
     * If the pointer goes below 85vh,
     * close the active submenu.
     */
    if (y >= window.innerHeight * MENU_DEACTIVATE_THRESHOLD) {
      if (this.#state.activeItem) {
        this.#deactivate();
      }

      return;
    }

    /*
     * Find the real element underneath the pointer.
     */
    const target = document.elementFromPoint(x, y);

    if (!target) return;

    /*
     * Find a top-level menu item.
     */
    const menuItem = target.closest(
      '.menu-list__list-item'
    );

    if (!menuItem) return;

    /*
     * Make sure this menu item belongs to THIS header-menu.
     */
    if (!this.contains(menuItem)) return;

    /*
     * Ignore the "More" overflow slot here.
     * Horizon already handles that separately.
     */
    if (menuItem.matches('[slot="more"]')) {
      return;
    }

    /*
     * Make sure it has a real menu link.
     */
    const menuLink =
      menuItem.querySelector('[ref="menuitem"]');

    if (!menuLink) return;

    /*
     * If this is already the active item,
     * nothing else is required.
     */
    if (this.#state.activeItem === menuLink) {
      return;
    }

    /*
     * Trigger the same activation mechanism
     * used by the native pointerenter event.
     */
    menuItem.dispatchEvent(
      new PointerEvent('pointerenter', {
        bubbles: false,
        clientX: x,
        clientY: y,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        isPrimary: event.isPrimary,
      })
    );
  };

  /**
   * =========================================================
   * EXISTING SAFETY BOX POINTER TRACKING
   * =========================================================
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    /*
     * Close if pointer moves below 85vh.
     */
    if (
      event.clientY >=
      window.innerHeight * MENU_DEACTIVATE_THRESHOLD
    ) {
      this.#deactivate();
      return;
    }

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeLink.dataset.safetyBox =
      `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox =
            'false';

          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Reconcile pointer target.
   *
   * Important for Safari and cases where the pointer
   * enters the menu from an unusual direction.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;

    /*
     * Don't reconcile when pointer is below 85vh.
     */
    if (
      y >=
      window.innerHeight * MENU_DEACTIVATE_THRESHOLD
    ) {
      this.#deactivate();
      return;
    }

    requestAnimationFrame(() => {
      const target =
        document.elementFromPoint(x, y);

      if (!target) return;

      const listItem =
        target.closest(
          '.menu-list__list-item'
        );

      if (!listItem) return;

      if (!this.contains(listItem)) return;

      if (listItem.matches('[slot="more"]')) {
        return;
      }

      const menuLink =
        listItem.querySelector(
          '[ref="menuitem"]'
        );

      if (
        menuLink &&
        menuLink !== this.#state.activeItem
      ) {
        listItem.dispatchEvent(
          new PointerEvent(
            'pointerenter',
            {
              bubbles: false,
              clientX: x,
              clientY: y,
            }
          )
        );
      }
    });
  }

  /**
   * Start pointer tracking.
   */
  #startPointerTracking(
    item,
    previousItem
  ) {
    if (previousItem) {
      this.#stopPointerTracking(
        previousItem
      );
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove,
        { passive: true }
      );
    }

    const rect =
      item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent?.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const boundary = isOverlap
      ? this.headerComponent?.querySelector(
          '.header__row--top'
        )
      : this.headerComponent;

    item.style.setProperty(
      '--box-height',
      `${
        (boundary?.getBoundingClientRect().bottom ?? 0) -
        rect.top
      }px`
    );
  }

  /**
   * Stop pointer tracking.
   */
  #stopPointerTracking(item) {
    window.clearTimeout(
      this.#pointerIdleTimer
    );

    this.#pointerIdleTimer =
      undefined;

    item.style.removeProperty(
      '--box-height'
    );

    delete item.dataset.safetyBox;
  }

  /**
   * Get overflow menu.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow"]'
        )
    );
  }

  /**
   * Whether overflow list is hovered.
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow-list"]'
        )
        ?.matches(':hover') ?? false
    );
  }

  /**
   * Header component.
   */
  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * =========================================================
   * ACTIVATE
   * =========================================================
   */
  activate = (event) => {
    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const item =
      findMenuItem(event.target);

    if (
      !item ||
      item === this.#state.activeItem
    ) {
      return;
    }

    const isDefaultSlot =
      event.target.slot === '';

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded =
        'false';
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';

    item.ariaExpanded = 'true';

    let submenu =
      findSubmenu(item);

    const hasSubmenu =
      Boolean(submenu);

    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu =
        this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(
        this.#hoverDispatchTimer
      );

      this.#hoverDispatchTimer =
        undefined;

      const committedItem =
        item;

      if (
        event instanceof FocusEvent
      ) {
        this.dispatchEvent(
          new MegaMenuHoverEvent()
        );
      } else {
        this.#hoverDispatchTimer =
          setTimeout(() => {
            this.#hoverDispatchTimer =
              undefined;

            if (
              this.#state.activeItem ===
              committedItem
            ) {
              this.dispatchEvent(
                new MegaMenuHoverEvent()
              );
            }
          },
          HOVER_COMMIT_DELAY_MS
        );
      }

      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                submenu.offsetHeight > 0
              ) {
                this.headerComponent?.style.setProperty(
                  '--submenu-height',
                  `${submenu.offsetHeight}px`
                );

                this.#cleanupMutationObserver();
              }
            });
          });
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight =
      submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight ||
          0;

        finalHeight =
          Math.max(
            overflowHeight,
            overflowListHeight
          );
      } else {
        finalHeight =
          overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight =
      this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );

    this.#startPointerTracking(
      item,
      previouslyActiveItem
    );
  };

  /**
   * =========================================================
   * DEACTIVATE
   * =========================================================
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const menu =
      findSubmenu(
        this.#state.activeItem
      );

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(
        document.activeElement
      );

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(
        event.relatedTarget
      );

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches(
        '[slot="overflow"]'
      );

    if (
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (
        this.#state.activeItem
      ) {
        this.#stopPointerTracking(
          this.#state.activeItem
        );
      }

      return;
    }

    /*
     * Don't immediately close because the pointer
     * may still be travelling inside the 85% zone.
     */
    if (
      this.#lastPointer.y <
      window.innerHeight *
        MENU_DEACTIVATE_THRESHOLD
    ) {
      return;
    }

    this.#deactivate();
  }

  /**
   * Immediate deactivation.
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer =
      undefined;

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(
      0,
      0
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    const submenu =
      findSubmenu(item);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem =
      null;

    this.ariaExpanded =
      'false';

    item.ariaExpanded =
      'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Get overflow list height.
   */
  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu
          ?.offsetHeight || 0
      );
    }

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach(
        (link) => {
          const submenu =
            link.querySelector(
              '[ref="submenu[]"]'
            );

          if (submenu) {
            cb(submenu);
          }
        }
      );
    };

    mapSubmenus(
      (submenu) => {
        submenu.style.setProperty(
          'display',
          'none'
        );
      }
    );

    const height =
      this.overflowMenu
        ?.offsetHeight || 0;

    mapSubmenus(
      (submenu) => {
        submenu.style.removeProperty(
          'display'
        );
      }
    );

    return height;
  }

  /**
   * Get visible header height.
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) {
      return 0;
    }

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    return isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (
          this.headerComponent.querySelector(
            '.header__row--top'
          )
        )?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Set full open header height.
   */
  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          headerVisibleHeight;

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images.
   */
  #preloadImages = () => {
    const images =
      this.querySelectorAll(
        'img[loading="lazy"]'
      );

    images?.forEach(
      (image) => {
        image.removeAttribute(
          'loading'
        );
      }
    );
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver
      ?.disconnect();

    this.#submenuMutationObserver =
      null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find menu item.
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  if (
    element.matches(
      '[slot="more"]'
    )
  ) {
    return findMenuItem(
      element.parentElement
        ?.querySelector(
          '[slot="overflow"]'
        )
    );
  }

  return element.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * Find submenu.
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}